import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, { useLayoutEffect, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './TwoColumnRow.module.scss';
import useMainContentWidthSelector from '../../utils/useMainContentWidthSelector';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const ASIDE_WIDTH = parseInt(styles.asideWidth, 10);

const BREAKPOINTS = [
  ['largerThanAsideShown', parseInt(styles.minAsideShownWidth, 10)],
  ['largerThanDesktop', parseInt(styles.minDesktopWidth, 10)],
  ['largerThanTablet', parseInt(styles.minTabletWidth, 10)],
] as const;

type TwoColumnRowProps =
  | {
      /**
       * A node that spans the full width of the row
       */
      full: ReactNode;
    }
  | {
      main: ReactNode | ((isAsideShown: boolean) => ReactNode);
      aside?: ReactNode;
      onIsAsideShownChange?: (isAsideShown: boolean) => void;
      asideLocation?: 'right' | 'left';
    };

/**
 * A component that renders a row with a main section and an optional aside section.
 * The main section appears on the left, and the aside section appears on the right.
 * The aside section is hidden on smaller screens.
 * The component also supports a full prop that renders content spanning the entire width of the row.
 *
 * Implementation notes:
 * - This component uses a JavaScript-based approach to avoid rendering
 *   the aside section when it is not needed.
 * - Rather than using ResizeObserver to monitor the container width, we rely on
 *   useMainContentWidthSelector. This prevents the aside section from briefly
 *   appearing and being hidden on mobile screens during the sidebar's entry animation.
 */
const TwoColumnRow = (props: TwoColumnRowProps) => {
  const breakpoints = useMainContentWidthSelector((width) =>
    BREAKPOINTS.filter(([, breakpointWidth]) => width >= breakpointWidth).map(
      ([name]) => name,
    ),
  );

  const shouldShowAside = breakpoints.includes('largerThanAsideShown');
  const [hasAsideShown, setHasAsideShown] = useState(shouldShowAside);

  if (shouldShowAside && !hasAsideShown) {
    setHasAsideShown(true);
  }

  const isAsideShownChangeEvent = useEffectEvent((isAsideShown: boolean) => {
    if ('onIsAsideShownChange' in props && props.onIsAsideShownChange) {
      props.onIsAsideShownChange(isAsideShown);
    }
  });

  useLayoutEffect(() => {
    isAsideShownChangeEvent(shouldShowAside);
  }, [shouldShowAside]);

  return (
    <div
      className={classNames(
        styles.row,
        breakpoints.map((name) => styles[name]),
      )}
    >
      {'full' in props ? (
        <div className={styles.full}>{props.full}</div>
      ) : (
        <>
          {props.asideLocation === 'left' && (
            <div className={styles.aside}>{hasAsideShown && props.aside}</div>
          )}
          <div className={styles.main}>
            {typeof props.main === 'function'
              ? props.main(shouldShowAside)
              : props.main}
          </div>
          {props.asideLocation !== 'left' && (
            <div className={styles.aside}>{hasAsideShown && props.aside}</div>
          )}
        </>
      )}
    </div>
  );
};

export default TwoColumnRow;
